<template>
  <div
    :class="[
      'generic-page',
      colorModeClass,
      {
        '--with-fitter-badge': showFitterBadge,
        '--content-type-workout': isWorkoutPage,
        '--with-comments': is_commentable
      }
    ]"
  >
    <toolbar
      v-if="showToolbar"
      :transparent="renderFixedToolbar"
      :animate-bg-to-white="isWorkoutPage"
      :use-account-styles="useAccountStyles"
      :back="isHomepage ? false : is_root ? !!show_back_button : true"
      :emit-on-exit-attempt="isWorkoutPage"
      :logo="isHomepage ? true : useGlobalToolbar"
      fixed
      with-menu
      @exitAttempted="exitAttempted"
    >
      <template v-slot:right="{ textColor }">
        <template v-if="captureFormId">
          <submissions-button
            :form-id="captureFormId"
            :title="page_title"
            :is-missable="isWorkoutPage"
          />

          <div id="toolbar-submission-button-portal" />
        </template>

        <add-to-schedule-button
          v-if="show_add_to_schedule_button"
          :item-id="page_id"
          item-type="Page"
          :page-content-type="page_content_type"
          class="mr-1"
        />

        <bookmark-button
          v-if="isHomepage ? false : show_bookmark_button"
          :page-id="page_id"
          :style="useAccountStyles ? { color: textColor } : {}"
        />

        <div id="page-toolbar-actions" />
      </template>
    </toolbar>

    <template v-if="showPageContent">
      <blocks-container
        :blocks="blocks"
        :page-id="page_id"
        @init="handleContainerInit"
      />

      <cta-row
        v-if="showCtaRow"
        :key="page_id"
        white-bg
        overlay
        :classes="isWorkoutPage ? 'generic-page__cta-row--workout-offset' : ''"
      >
        <div class="w-100">
          <template v-if="is_commentable || isPresent(showThread)">
            <message-thread
              :key="threadId"
              class="generic-page__message-thread"
              placeholder="Add a comment..."
              collapsable
              with-participants
              is-comment-thread
              :message-thread-id="threadId"
            />
          </template>
        </div>
        <template v-if="displayablePageActions.length > 0">
          <template v-for="action in displayablePageActions">
            <template
              v-if="action.action_type === PAGE_ACTION_TYPES.MARK_COMPLETE"
            >
              <mark-complete
                :key="action.id"
                :block-action="action.block_action"
                :page-title="page_title"
                :page-id="page_id"
                :root-id="root_id"
              />
            </template>

            <template
              v-else-if="
                action.action_type === PAGE_ACTION_TYPES.GENERIC_BUTTON
              "
            >
              <generic-button
                :key="action.id"
                :block-action="action.block_action"
                :text="action.text"
                :post-purchase-text="action.post_purchase_text"
              />
            </template>

            <template
              v-else-if="action.action_type === PAGE_ACTION_TYPES.START"
            >
              <start
                :key="action.id"
                :page-title="page_title"
                :page-id="page_id"
                :text="action.text"
                :schedule="page_schedule"
                :access="page_access"
                :page-type="page_content_type"
                :action="action"
              />
            </template>
          </template>
        </template>

        <!-- <page-siblings v-if="siblings.length > 1" :pages="siblings" /> -->
      </cta-row>
    </template>

    <template v-else-if="!hasCheckedAuthentication || isFetchingAccess">
      <div class="container pt-1">
        <placeholder-rect width="100%" height="250px" />
        <placeholder-rect width="80%" height="32px" />
        <placeholder-rect width="35%" height="32px" />
      </div>
    </template>

    <template v-else>
      <div class="container">
        <div
          class="text-center h-full px-3 d-flex flex-column justify-content-center"
        >
          <v-icon icon="lock" class="text-grey-50" size="3rem" />
          <h2 class="mb-0">You don't have access</h2>

          <template v-if="isPrivate && !hasAccess">
            <p class="lead mt-2 mb-5">
              If you should have access to this content you'll need to login or
              contact the app owner for access
            </p>
          </template>
          <template v-else-if="isLockedFromPurchase">
            <p class="lead mt-2 mb-5">
              To unlock this content, you can make a purchase using the button
              below
            </p>
          </template>
          <template v-else>
            <p class="lead mt-2 mb-5">
              Please login to view this content
            </p>
          </template>
          <v-button
            v-if="isLockedFromPurchase"
            block
            @click="triggerPurchase"
            class="mb-2"
          >
            Purchase
          </v-button>

          <v-button
            v-if="!isAuthenticated"
            secondary
            @click="() => $router.push('#auth')"
          >
            Log In
          </v-button>
        </div>
      </div>
    </template>

    <page-action-panel-modal :page-id="page_id" />
    <v-made-with-movement v-if="!isDesktop" :color-mode-override="color_mode" />
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from "vuex"

import isPreviewContext from "@/lib/is-platform/is-preview-context"
import { isPresent, isEmpty, isBoolean } from "@/lib/utils"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { pageMeta } from "@/lib/pwa-meta"
import { QUERY_PARAMS } from "@/lib/messages-utils"
import { BLOCK_TYPES, COLOR_MODES } from "@/lib/stream-utils"

import BlocksContainer from "@/components/stream/BlocksContainer"
import Toolbar from "@/components/Toolbar"
import PlaceholderRect from "@/components/PlaceholderRect"

const PAGE_ACTION_TYPES = {
  MARK_COMPLETE: "mark_complete",
  GENERIC_BUTTON: "generic_button",
  START: "start"
}

export default {
  components: {
    BlocksContainer,
    Toolbar,
    PlaceholderRect,
    CtaRow: () => import("@/components/CtaRow"),
    // PageSiblings: () => import("@/components/toolbar/PageSiblings"),
    PageActionPanelModal: () =>
      import("@/components/modals/PageActionPanelModal"),
    BookmarkButton: () => import("@/components/toolbar/BookmarkButton"),
    AddToScheduleButton: () =>
      import("@/components/toolbar/AddToScheduleButton"),
    SubmissionsButton: () => import("@/components/toolbar/SubmissionsButton"),
    Start: () => import("@/components/page-actions/Start"),
    MarkComplete: () => import("@/components/page-actions/MarkComplete"),
    GenericButton: () => import("@/components/page-actions/GenericButton"),
    MessageThread: () => import("@/components/message-thread/Thread")
  },

  props: {
    page_id: { type: [Number, String], required: true },
    root_id: { type: [Number, String], default: null },
    page_title: { type: String, default: "" },
    page_content_type: { type: String, default: null },
    page_data: { type: Object, default: () => ({}) }, // TODO: Remove
    attribute_values: { type: Object, default: () => ({}) },
    page_access: { type: Object, default: () => ({}) },
    page_schedule: { type: Object, default: () => ({}) },
    page_actions: { type: Array, default: () => [] },
    siblings: { type: Array, default: () => [] },
    blocks: { type: Array, required: true },
    is_root: { type: Boolean, default: false },
    show_back_button: { type: Boolean, default: true },
    show_bookmark_button: { type: Boolean, default: true },
    show_add_to_schedule_button: { type: Boolean, default: false },
    show_bottom_padding: { type: Boolean, default: true },
    color_mode: { type: String, default: "" },
    use_global_toolbar: { type: Boolean, default: false },
    og_title: { type: String, default: "" },
    og_description: { type: String, default: "" },
    og_image: { type: Object, default: null },
    is_commentable: { type: Boolean, default: false },
    message_thread_id: { type: [String, Number], default: null }, // TODO: Remove
    content_root_id: { type: [Number, String], default: null }
  },

  data() {
    return {
      showThread: null,
      PAGE_ACTION_TYPES
    }
  },

  provide() {
    return {
      page_id: this.page_id
    }
  },

  head() {
    return pageMeta({
      title: isPresent(this.og_title) ? this.og_title : this.page_title,
      ogTitle: isPresent(this.og_title) ? this.og_title : this.page_title,
      url: this.global?.app.url + this.page_data["page.path"],
      description: isPresent(this.og_description) ? this.og_description : null,
      image: isPresent(this.og_image) ? this.og_image.image : null
    })
  },

  computed: {
    ...mapState(["global"]),
    ...mapState("notifications", ["canAsk", "token"]),
    ...mapState("responsive", ["isDesktop"]),
    ...mapGetters("pages", [
      "hasPageAccess",
      "isToolbarHidden",
      "submissionsCountByPage",
      "hasFetchedAccess",
      "getPageAccess"
    ]),
    ...mapGetters("auth", ["hasCheckedAuthentication", "isAuthenticated"]),
    ...mapGetters(["showFitterBadge"]),
    ...mapGetters("workouts", ["workoutIsFollowAlongVisible"]),

    useAccountStyles() {
      return this.isHomepage ? true : this.useGlobalToolbar
    },

    showToolbar() {
      if (isPreviewContext()) return false

      if (this.isWorkoutPage && this.isToolbarHidden(this.page_id)) {
        return false
      }

      return this.isHomepage && this.isDesktop
        ? false
        : this.isHomepage
        ? isPresent(this.global) &&
          isPresent(this.global.toolbar) &&
          !this.global.toolbar.is_hidden
        : true
    },

    showPageContent() {
      return isPreviewContext() ||
        this.isPublic ||
        (this.isRequirePurchase && this.allowBrowsing) ||
        (this.isRequireAuthentication && this.allowBrowsing)
        ? true
        : this.hasAccess
    },

    allowBrowsing() {
      return isBoolean(this.page_access.allow_browsing)
        ? this.page_access.allow_browsing
        : true
    },

    isPrivate() {
      return (
        isPresent(this.page_access) &&
        this.page_access.inherit === false &&
        this.page_access.is_private
      )
    },

    isPublic() {
      return (
        isPresent(this.page_access) &&
        this.page_access.inherit === false &&
        this.page_access.is_private === false &&
        this.page_access.require_authentication === false
      )
    },

    isRequirePurchase() {
      return (
        isPresent(this.page_access) &&
        this.page_access.inherit === false &&
        this.page_access.require_purchase === true
      )
    },

    isRequireAuthentication() {
      return (
        isPresent(this.page_access) &&
        this.page_access.inherit === false &&
        this.page_access.require_authentication === true
      )
    },

    isLockedFromPurchase() {
      return (
        isPresent(this.pageAccess) &&
        !this.hasAccess &&
        this.pageAccess.data?.require_purchase
      )
    },

    pageAccess() {
      return this.getPageAccess(this.page_id)
    },

    hasAccess() {
      return !!this.hasPageAccess(this.page_id)
    },

    isWorkoutPage() {
      return (
        this.page_content_type === "workout" && isPresent(this.workoutBlock)
      )
    },

    showCtaRow() {
      return (
        this.is_commentable ||
        isPresent(this.showThread) ||
        this.displayablePageActions.length > 0 ||
        (this.siblings.length > 0 &&
          (this.isWorkoutPage ? !this.workoutIsFollowAlongVisible : true))
      )
    },

    displayablePageActions() {
      return this.isWorkoutPage
        ? []
        : this.page_actions.filter(a => a.action_type !== "track_progress")
    },

    useGlobalToolbar() {
      return this.is_root ? this.use_global_toolbar : false
    },

    isHomepage() {
      return this.is_root && this.$route.path === "/"
    },

    renderFixedToolbar() {
      // TODO: think of better solution to change it for follow along
      return this.isWorkoutPage
        ? !this.workoutIsFollowAlongVisible
        : this.isHomepage
        ? false
        : !this.useGlobalToolbar
    },

    submissionsCount() {
      return this.submissionsCountByPage(this.page_id)
    },

    workoutBlock() {
      return this.blocks.find(b => b.type === BLOCK_TYPES.WORKOUT_BLOCK)
    },

    captureFormId() {
      if (this.isWorkoutPage) {
        if (this.workoutBlock && this.workoutBlock.data) {
          return this.workoutBlock.data.workout.capture_form_id
        }
      }

      return null
    },

    colorModeClass() {
      return isEmpty(this.color_mode)
        ? ""
        : this.color_mode === COLOR_MODES.DARK
        ? "--dark-mode"
        : "--light-mode"
    },

    isFetchingAccess() {
      return !this.hasFetchedAccess(this.page_id)
    },

    threadId() {
      return isPresent(this.message_thread_id)
        ? this.message_thread_id
        : this.showThread
    }
  },

  activated() {
    this.setPage()
  },

  mounted() {
    this.setPage()

    // Evaluate on mount to prevent thread from disappearing when the query param is removed
    this.showThread = this.$route?.query[QUERY_PARAMS.SHOW_THREAD]
  },

  methods: {
    isPresent,
    isPreviewContext,

    ...mapMutations("pages", [
      "setPageData",
      "setPageAttributeValues",
      "setPageIsBookmarked",
      "setCurrentPage"
    ]),
    ...mapActions("pages", ["addToAccessQueue"]),
    ...mapActions("pageActionPanel", ["triggerAction"]),

    fetchAccess(initial = false) {
      this.addToAccessQueue(this.page_id)

      setTimeout(() => {
        if (initial && this.isPublic && this.$route.hash === "#auth") {
          // Allows a page to be public even with require authentication
          this.$router.removeHash()
        }
      }, 1000)
    },

    handleContainerInit() {
      this.$emit("blocksRendered")
    },

    exitAttempted() {
      EventBus.$emit(EVENTS.PAGE_EXIT_ATTEMPTED, { pageId: this.page_id })
    },

    triggerPurchase() {
      EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
        productIds: this.pageAccess.data.product_ids,
        overlay: this.pageAccess.data.purchase_overlay
      })
    },

    setPage() {
      this.setCurrentPage({
        id: this.page_id,
        rootId: this.root_id,
        contentRootId: this.content_root_id,
        path: this.page_data["page.path"],
        actions: this.page_actions,
        type: this.page_content_type
      })
    }
  },

  watch: {
    hasCheckedAuthentication: {
      immediate: true,
      handler() {
        if (this.hasCheckedAuthentication) {
          this.fetchAccess(true)
        }
      }
    },

    isAuthenticated() {
      if (this.isAuthenticated) {
        this.fetchAccess()
      }
    },

    // TODO: Remove
    page_data: {
      immediate: true,
      deep: true,
      handler() {
        this.setPageData({
          pageId: this.page_id,
          data: this.page_data
        })
      }
    },

    attribute_values: {
      immediate: true,
      deep: true,
      handler() {
        this.setPageAttributeValues({
          pageId: this.page_id,
          values: this.attribute_values
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.generic-page {
  background: $color-ui--background;
  color: $color-ui--grey-90;

  &.--content-type-workout {
    padding-bottom: 80vh;

    &.--with-fitter-badge {
      padding-bottom: 60vh;
    }
  }

  // Safe Space Top - No Toolbar
  & > .blocks-container {
    & > .block:first-child {
      padding-top: env(safe-area-inset-top);

      &[data-type="hero_block"],
      &[data-type="livestream_block"] {
        padding-top: inherit;
      }
    }
  }

  // Safe Space Top - Inline Toolbar
  & > [data-role="inline-toolbar"] + .blocks-container {
    & > .block:first-child {
      padding-top: inherit;
    }
  }

  // Safe Space Top - Fixed Toolbar
  // Max 5 levels of nesting supported
  & > [data-transparent-toolbar] + .blocks-container {
    & > .block:first-child,
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child, // .block:first-child > composed-block > .block
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child, // .block:first-child > composed-block > composed-block > .block
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child, // .block:first-child > composed-block > composed-block > composed-block > .block
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child, // .block:first-child > composed-block > composed-block > composed-block > composed-block > .block
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child // .block:first-child > composed-block > composed-block > composed-block > composed-block > composed-block > .block
 {
      padding-top: 60px;
      padding-top: calc(60px + env(safe-area-inset-top));

      &[data-type="hero_block"],
      &[data-type="iframe_block"],
      &[data-type="livestream_block"],
      &[data-type="map_block"],
      &[data-type="composed_block"] {
        padding-top: inherit;
      }

      &[data-type="image_block"] {
        padding-top: env(safe-area-inset-top);
      }
    }
  }
}

.generic-page__message-thread {
  &.message-thread {
    min-height: initial;
    max-height: 68vh;

    .overlay-pill {
      display: flex;
      flex-direction: row-reverse;
      color: $color-ui--grey-60;
      background: transparent;
      backdrop-filter: none;

      .v-icon {
        margin-left: 0.5rem;
      }
    }

    .thread__footer {
      border: none;
      margin: 0;
      width: 100%;
      box-shadow: none;
      border: 2px solid $color-ui--grey-30;
      max-width: initial;
    }

    .message-thread__content {
      padding-top: 0;
      padding-bottom: 0;
    }

    &.--comments {
      flex-direction: column;

      .message-thread__footer-wrapper {
        margin: 0 0 0.75rem;
        padding-top: 0;
      }
    }

    &.--thread {
      & > .message-thread__content-wrapper {
        padding-left: 0;

        .thread__line-vert {
          left: 16px;
          top: 42px;
          height: 100%;
        }

        .thread__corner {
          display: none;
        }

        .message-thread__content {
          padding-left: 1px;
        }
      }

      .message-thread__actions {
        margin-bottom: 0.5rem;

        .v-button {
          margin-left: 2px;
          margin-bottom: -4px;
        }
      }

      .thread__footer {
        .thread__line-vert {
          height: 12px;
          top: -10px;
          background: $color-ui--grey-20;
        }
      }
    }
  }
}

.generic-page__cta-row--workout-offset {
  transform: translateY(-68px) !important;
  z-index: 14 !important;
}
</style>
